import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Typography, Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import RunningWithErrorsRoundedIcon from '@mui/icons-material/RunningWithErrorsRounded';
import AltRouteRoundedIcon from '@mui/icons-material/AltRouteRounded';
import EmphasisPopover from '@src/containers/EmphasisPopover';
import Tooltip from '@src/components/Tooltip';
import SpeedNewV2 from '@src/new-components/Speed';
import BadgeAvatar from '@src/components/BadgeAvatar';
import BreakTimeNew from '@src/new-components/BreakTime';
import { convertFormatDate } from '@src/utils/date';
import { SUB_STATUS } from '@src/constants/voiceCloning';
import LimitedFeature from '@src/components/LimitedFeature';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import SpeakerOption from '../Dubbing/SpeakerOption';

const ConfigOptionBlock = ({
  handleOpenVoices,
  handleChangeSpeed,
  handleChangeBreakTime,
  handleChangeEmphasis,
  voice,
  speed,
  breakTime,
  hasEmphasis,
  emphasis,
  isSynthesizing,
  onSplitBlock,
  canSplitBlock,
  splitBlockDisableReason,
  isDubbingProject,
  openVoice,
  speakerName,
  speakerConfigEnabled,
  isBlockActive,
  isBlockUIImprovement,
  isGeneratingBlock,
}) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);

  const isInValidPublicVoiceCloning =
    voice?.userId !== user?.id &&
    voice?.discardAt &&
    moment().isAfter(voice?.discardAt);

  const isPublicVoiceCloning = voice?.status === SUB_STATUS.PUBLIC;
  const isVoiceCloningAboutToRemoved =
    isPublicVoiceCloning && voice?.discardAt
      ? moment().isBefore(voice?.discardAt)
      : false;

  return (
    <div className="config-toolbar">
      {isDubbingProject && speakerConfigEnabled ? (
        <SpeakerOption
          handleOpenVoices={handleOpenVoices}
          isSynthesizing={isSynthesizing}
          voice={voice}
          isInValidPublicVoiceCloning={isInValidPublicVoiceCloning}
          isVoiceCloningAboutToRemoved={isVoiceCloningAboutToRemoved}
          openVoice={openVoice}
          speakerName={speakerName}
        />
      ) : (
        <div className="config-toolbar-voice">
          <Tooltip arrow title={t('chooseVoice')} placement="top">
            {/* The disabled <button> ignores cursor styling, so the
                not-allowed cursor lives on a wrapping span (the button keeps
                pointer-events:none, letting hover fall through to the span). */}
            <span
              style={
                isSynthesizing
                  ? { cursor: 'not-allowed', display: 'inline-flex' }
                  : { display: 'inline-flex' }
              }
            >
              <Button
                onClick={handleOpenVoices}
                className="choose-voice"
                data-id="open-voice-list"
                disabled={isSynthesizing}
                sx={{
                  // Match the disabled look of the sibling controls.
                  '&.Mui-disabled': { opacity: 0.5 },
                }}
              >
                <BadgeAvatar
                  width={25}
                  smallImgWidth={10}
                  img={voice?.roundImage}
                  smallImg={voice?.language?.roundImage}
                  type="image"
                />
                <Typography
                  variant="body2"
                  className={
                    isInValidPublicVoiceCloning
                      ? 'invalid-voice-name'
                      : 'voice-name'
                  }
                >
                  {voice && voice.name}
                </Typography>
              </Button>
            </span>
          </Tooltip>
          <LimitedFeature voiceFeatures={voice?.features} />
          {isVoiceCloningAboutToRemoved && (
            <Tooltip
              arrow
              title={t('voiceCloningDiscardAt', {
                date: convertFormatDate(voice?.discardAt),
              })}
              placement="top"
            >
              <RunningWithErrorsRoundedIcon className="returning-error-icon" />
            </Tooltip>
          )}
          {isInValidPublicVoiceCloning && (
            <Tooltip
              arrow
              title={t('invalidPublicVoiceCloning')}
              placement="top"
            >
              <RunningWithErrorsRoundedIcon className="returning-error-icon" />
            </Tooltip>
          )}
        </div>
      )}
      {(!isBlockUIImprovement || isBlockActive) && (
        <>
          <SpeedNewV2
            currentSpeed={speed}
            disabled={isSynthesizing}
            onChange={handleChangeSpeed}
          />
          {hasEmphasis && (
            <EmphasisPopover
              currentEmphasis={emphasis || 0}
              onChangeEmphasis={handleChangeEmphasis}
              isDisabled={isSynthesizing}
            />
          )}
          {!isDubbingProject && (
            <BreakTimeNew
              currentBreakTime={breakTime}
              disabled={isSynthesizing || isGeneratingBlock}
              onChange={handleChangeBreakTime}
              width="45px"
            />
          )}
          {onSplitBlock && (
            <CommonButton
              variant="outlined"
              size="small"
              onMouseDown={(e) => e.preventDefault()}
              onClick={onSplitBlock}
              disabled={!canSplitBlock}
              className="split-block-btn"
              style={{
                borderRadius: '12px',
                padding: '4px 6px',
                minWidth: 'auto',
                textTransform: 'none',
                border: 'none',
              }}
            >
              <Tooltip
                arrow
                title={splitBlockDisableReason || t('splitBlockTooltip')}
                placement="top"
              >
                <Box display="flex" alignItems="center" gap="4px">
                  <AltRouteRoundedIcon
                    style={{
                      fontSize: '24px',
                      color: COLOR.secondary[8],
                    }}
                  />
                  <Typography
                    variant="body2"
                    style={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: COLOR.secondary[8],
                    }}
                  >
                    {t('splitBlock')}
                  </Typography>
                </Box>
              </Tooltip>
            </CommonButton>
          )}
        </>
      )}
    </div>
  );
};

export default ConfigOptionBlock;
